import type { Citation, MessageParams, RunOutcome, RunStatus, StreamEvent } from '@ralph/contracts'

/**
 * Why a run or one of its steps failed, as identifiers rather than as a sentence.
 *
 * `code` is the machine class the surfaces branch on — ChatPage closes the composer on
 * `agent_unavailable` — and it is coarse on purpose. `messageKey` names the SENTENCE, so the
 * conversation and the trace lane can render the refusal in the reader's language with its
 * specifics intact, and `message` stays as the server's English: the operator's line, and the
 * fallback for a failure nothing has keyed yet (PRD-27 D-4, F-1).
 *
 * Structurally the renderable half of an `ApiIssue`, which is what `describeIssue` takes — so
 * lifting a key off the wire is an assignment rather than a conversion.
 */
export type RunFailure = {
  code: string
  message: string
  messageKey?: string | null
  params?: MessageParams | null
}

/**
 * One node in the trace lane.
 *
 * The trace is the product's signature surface, so a node carries everything the panel
 * needs to render without a second fetch: what kind of work it was, how long it took, what
 * it cost, and the type-specific detail (which model, which passages, which arguments).
 */
export type TraceNode = {
  id: string
  index: number
  kind: 'model_call' | 'retrieval' | 'tool_call' | 'approval'
  /**
   * The server's English caption, always present and never discarded.
   *
   * It is what a step logged before PRD-27 has instead of a code, and it is the fallback for
   * a code this build has not learned — which is why the server keeps sending it (D-8).
   */
  label: string
  /**
   * The same caption named rather than spelled, so the panel can render it in the reader's
   * language. Widened to `string` on the way in for the same reason `risk` and `dimension`
   * are: a server one deploy ahead may name a code this build cannot render, and that must
   * fall back to `label` rather than fail to parse.
   */
  labelCode?: string | null
  labelParams?: MessageParams | null
  status: 'running' | 'completed' | 'failed'
  durationMs: number | null
  model?: { provider: string; model: string; alias: string; routingReason: string; toolsOffered: string[] }
  segments?: Array<{ label: string; title: string; chars: number; estimatedTokens: number }>
  usage?: {
    inputTokens: number
    outputTokens: number
    cacheReadTokens: number
    costUsd: number
    timeToFirstTokenMs: number | null
    /** First ANSWER token, ms from request start; the thinking/writing boundary on the tape. */
    timeToFirstTextMs: number | null
    latencyMs: number
    finishReason: string
  }
  retrieval?: {
    lexicalCandidates: number
    semanticCandidates: number
    fusedCandidates: number
    selected: number
    results: Array<{
      chunkId: string
      documentTitle: string
      sourceName: string
      locator: string
      score: number
      lexicalRank?: number | null
      semanticRank?: number | null
      snippet: string
    }>
  }
  tool?: { name: string; displayName: string; risk: string; argumentsSummary: string; resultSummary?: string }
  error?: RunFailure
}

export type PendingApproval = {
  approvalId: string
  toolCallId: string
  tool: string
  displayName: string
  risk: string
  digest: string
  arguments: Record<string, unknown>
  rationale: string
  reversible: boolean
  dataLeavingPlatform: string[]
  expiresAt: string
}

export type RunStreamState = {
  runId: string | null
  status: RunStatus | 'idle'
  /** The answer as it arrives. Rendered incrementally so the user sees progress, not a spinner. */
  text: string
  thinking: string
  nodes: TraceNode[]
  citations: Citation[]
  approval: PendingApproval | null
  outcome: RunOutcome | null
  error: RunFailure | null
  lastSeq: number
  assistantMessageId: string | null
  budget: { maxSteps: number; maxCostUsd: number } | null
  /**
   * Budget dimensions the run has come close to exhausting, latest value per dimension.
   *
   * The server emits these as they happen and they used to fall through the reducer's
   * default case, which meant an answer could be cut short for a reason the person reading
   * it was never shown. "It stopped early" and "it stopped early because it hit its tool
   * budget" are different messages.
   */
  budgetWarnings: Array<{ dimension: string; used: number; limit: number }>
  /**
   * True when these events came from a replay of a finished run rather than a live one.
   * The trace panel treats both identically; the conversation body must not, or the
   * replayed answer renders a second time underneath the message it came from.
   */
  isReplay: boolean
  /**
   * True when the reader's saved preferences entered this run's context (AGM-092).
   *
   * Carried in state rather than derived, because the flag arrives once — on
   * `message.completed` — and the indicator that discloses it has to survive every later
   * event in the run. It is also the only place the client learns this: nothing on the
   * persisted message says memory was used, so a reducer that dropped the field (as this one
   * did) made the disclosure impossible to render rather than merely absent.
   */
  memoryInfluenced: boolean
}

export const initialState: RunStreamState = {
  runId: null,
  status: 'idle',
  text: '',
  thinking: '',
  nodes: [],
  citations: [],
  approval: null,
  outcome: null,
  error: null,
  lastSeq: -1,
  assistantMessageId: null,
  budget: null,
  budgetWarnings: [],
  isReplay: false,
  memoryInfluenced: false
}

/**
 * What happened to a send.
 *
 * `accepted` distinguishes "the server never took this message" from "the run started and
 * then something went wrong" — the two need opposite handling in the composer.
 */
export type SendResult = { ok: boolean; accepted: boolean }

export type Action =
  | { type: 'reset' }
  | { type: 'send-start' }
  | { type: 'replay-start' }
  | { type: 'event'; event: StreamEvent }
  | { type: 'error'; failure: RunFailure }
  | { type: 'cancelled' }

// Returns the SAME array when the node is absent, so an event for a step the panel never
// created (a step type it deliberately does not render) costs no re-render.
const upsertNode = (nodes: TraceNode[], id: string, patch: Partial<TraceNode>): TraceNode[] =>
  nodes.some((n) => n.id === id) ? nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) : nodes

export const reduce = (state: RunStreamState, action: Action): RunStreamState => {
  if (action.type === 'reset') return initialState
  /*
   * A send exists from the moment the button is pressed, not from the moment the network
   * confirms it — and the difference is a race that aborted real runs.
   *
   * `isStreaming` derives from `status`, and until this action existed the status stayed
   * `idle` from the press until the first SSE byte arrived. Everything that consults
   * "is a run in flight?" during that window got the wrong answer. The concrete casualty
   * was ChatPage's trace-hydration effect: a send changes its dependencies (it clears the
   * inspected run; the optimistic message changes the list), the effect ran inside the
   * window, saw an idle stream, and replayed the PREVIOUS run — whose first act is aborting
   * the in-flight fetch. The server kept running and billing the question; the client
   * reported it refused and handed the text back. On localhost the first byte usually won
   * the race and hid all of this; behind real network latency the effect won every time,
   * which surfaced as "my message disappears and the answer never streams".
   *
   * `queued` rather than a new status, because it is exactly what the server would say if
   * asked at that moment, and everything downstream (`isStreaming`, the composer's guard,
   * Stop) already treats it as in-flight.
   */
  if (action.type === 'send-start') return { ...initialState, status: 'queued' }
  if (action.type === 'replay-start') return { ...initialState, isReplay: true }
  if (action.type === 'error') {
    return { ...state, status: 'failed', error: action.failure }
  }
  /*
   * Stop. A settled state, not a failed one: the reader left the run, nothing went wrong, and
   * whatever text had arrived is still worth reading.
   *
   * Only a run that is still in flight can be stopped, and anything else returns the SAME
   * object — a Stop that races the closing event must not overwrite a real outcome with
   * "cancelled", and a second press must not cost a render. `status` is the only thing the
   * composer reads to decide it may send again, so leaving it at `running` (which is what
   * this action was invented to fix) wedges every later message in the conversation.
   */
  if (action.type === 'cancelled') {
    if (state.status !== 'running' && state.status !== 'queued') return state
    return { ...state, status: 'cancelled' }
  }

  const event = action.event
  // Replay after a reconnect re-delivers events we already applied. Dropping anything at or
  // below the high-water mark keeps the reducer idempotent, so text is never duplicated.
  if (event.seq <= state.lastSeq) return state
  const base = { ...state, lastSeq: event.seq }

  switch (event.type) {
    case 'run.started':
      return {
        ...base,
        runId: event.runId,
        status: 'running',
        assistantMessageId: event.assistantMessageId,
        budget: { maxSteps: event.budget.maxSteps, maxCostUsd: event.budget.maxCostUsd }
      }

    case 'run.status':
      return { ...base, status: event.status }

    case 'step.started': {
      // `policy_check` / `deterministic_transform` etc. are recorded server-side but are not
      // worth a lane node; the panel would become a log rather than a summary.
      const kind =
        event.stepType === 'model_call' || event.stepType === 'retrieval' || event.stepType === 'tool_call'
          ? event.stepType
          : null
      if (!kind) return base

      return {
        ...base,
        nodes: [
          ...base.nodes,
          {
            id: event.stepId,
            index: event.index,
            kind,
            label: event.label,
            // Both halves, verbatim. The reducer decides nothing about words: a hook runs
            // outside any component, so there is no locale to ask here (PRD-27 F-3).
            labelCode: event.labelCode ?? null,
            labelParams: event.labelParams ?? null,
            status: 'running',
            durationMs: null
          }
        ]
      }
    }

    case 'step.completed':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, { status: 'completed', durationMs: event.durationMs })
      }

    case 'step.failed':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, {
          status: 'failed',
          durationMs: event.durationMs,
          error: { code: event.code, message: event.message }
        })
      }

    case 'model.request':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, {
          model: {
            provider: event.provider,
            model: event.model,
            alias: event.alias,
            routingReason: event.routingReason,
            toolsOffered: event.toolsOffered
          },
          segments: event.segments
        })
      }

    case 'model.delta':
      return { ...base, text: base.text + event.text }

    case 'model.thinking_delta':
      return { ...base, thinking: base.thinking + event.text }

    case 'model.reset':
      // The provider call is being retried and will re-stream from the beginning. Keeping
      // what arrived would show the reader the failed attempt's half-sentence followed by
      // the successful attempt's full answer.
      return { ...base, text: '', thinking: '' }

    case 'model.usage':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, {
          usage: {
            inputTokens: event.inputTokens,
            outputTokens: event.outputTokens,
            cacheReadTokens: event.cacheReadTokens,
            costUsd: event.costUsd,
            timeToFirstTokenMs: event.timeToFirstTokenMs ?? null,
            timeToFirstTextMs: event.timeToFirstTextMs ?? null,
            latencyMs: event.latencyMs,
            finishReason: event.finishReason
          }
        })
      }

    case 'retrieval.completed':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, {
          retrieval: {
            lexicalCandidates: event.lexicalCandidates,
            semanticCandidates: event.semanticCandidates,
            fusedCandidates: event.fusedCandidates,
            selected: event.selected,
            results: event.results
          }
        })
      }

    case 'tool.proposed':
      return {
        ...base,
        nodes: upsertNode(base.nodes, event.stepId, {
          tool: {
            name: event.tool,
            displayName: event.displayName,
            risk: event.risk,
            argumentsSummary: event.argumentsSummary
          }
        })
      }

    case 'tool.completed':
      return {
        ...base,
        nodes: base.nodes.map((n) =>
          n.id === event.stepId && n.tool ? { ...n, tool: { ...n.tool, resultSummary: event.resultSummary } } : n
        )
      }

    case 'approval.required':
      return {
        ...base,
        status: 'waiting_for_approval',
        approval: {
          approvalId: event.approvalId,
          toolCallId: event.toolCallId,
          tool: event.tool,
          displayName: event.displayName,
          risk: event.risk,
          digest: event.digest,
          arguments: event.arguments,
          rationale: event.rationale,
          reversible: event.reversible,
          dataLeavingPlatform: event.dataLeavingPlatform,
          expiresAt: event.expiresAt
        }
      }

    case 'approval.resolved':
      return { ...base, approval: null }

    case 'citations':
      return { ...base, citations: event.citations }

    case 'message.completed':
      // Authoritative: the streamed deltas are a preview, and this is what was persisted.
      //
      // `memoryInfluenced` is optional on the wire: a deployment that predates AGM-092 sends
      // no field, and "we don't know" must render as no claim rather than as "memory was
      // used" — so it defaults to false rather than to anything truthy.
      return {
        ...base,
        text: event.content,
        citations: event.citations,
        memoryInfluenced: event.memoryInfluenced ?? false
      }

    case 'run.completed':
      return { ...base, status: event.outcome.status, outcome: event.outcome }

    /*
     * No `messageKey` here, nor on `step.failed` above: the stream events carry a code and the
     * server's English and nothing else yet, so these failures render as prose until the SSE
     * error shapes gain the key the HTTP envelope already has (PRD-27 D-4). The fallback is
     * the correct behaviour meanwhile, not a gap left open.
     */
    case 'run.failed':
      return { ...base, status: 'failed', outcome: event.outcome, error: { code: event.code, message: event.message } }

    case 'budget.warning':
      // One entry per dimension, latest value: a run that keeps warning about cost should
      // show the current number, not a growing list of the same warning.
      return {
        ...base,
        budgetWarnings: [
          ...base.budgetWarnings.filter((w) => w.dimension !== event.dimension),
          { dimension: event.dimension, used: event.used, limit: event.limit }
        ]
      }

    default:
      return base
  }
}
